
import React, { useMemo } from 'react';
import { useUrlFriendlyToParameters, useGetURL } from 'helpers/urlFriendly';
import Label from "components/general/Label";
import Loader from 'components/general/Loader';
import NotAvailable from 'components/common/NotAvailable';
import { useQuery } from "@tanstack/react-query";
import { getAthletes, getTeams } from 'data/Querys';
import Competitor from 'components/presentation/Competitor';
import useViewport from 'components/general/useViewPort';
import { ALL_URL, getDateTimeFormat } from 'helpers/utils';
import DateTime from 'components/presentation/DateTime';
import MirsTable from 'components/general/table/Table';
import { useGetH1 } from 'helpers/commonCodes';
import { Link } from 'react-router-dom';


export default function NocEntriesByEvent() {
    const { mirs_competition, mirs_language, mirs_discipline } = useUrlFriendlyToParameters();
    const dataReports = useGetH1({ mirs_competition: mirs_competition });
    let arrHasTeams = []
    //let arrHasTeams = ['BK3', 'BKB', 'HBL', 'FBL', 'HOC', 'VVO', 'VPO', 'RU7', 'SHO', 'SWA'];

    // disciplines with team entries report
    dataReports?.h1s
        .filter(r => r.orisNo === 'C31T')
        ?.forEach((r) => {
            arrHasTeams.push(r?.disciplineCode)
        })

    const isTeam = arrHasTeams.includes(mirs_discipline);
    const queryname = isTeam ? 'teams' : 'athletes';
    const queryFn = isTeam ? getTeams : getAthletes;

    const { data, isError, isLoading } = useQuery({
        queryKey: [queryname, mirs_competition, mirs_language, 'A'],
        queryFn: queryFn,
        enabled: true,
        refetchInterval: 0,
        staleTime: Infinity,
        refetchIntervalInBackground: true,
        retry: false,
        suspense: false
    });

    if (isError) {
        return <NotAvailable label="LabelNoDataAvailable" />;
    }
    if (isLoading) {
        return <Loader />;
    }

    return <NocEntriesByEvent2 data={data} isTeam={isTeam}/>

}

function NocEntriesByEvent2 ({data, isTeam}) {
    const params =  useUrlFriendlyToParameters()
    const { mirs_discipline, mirs_noc } = params;
    const { breakPoint } = useViewport();
    const getURL = useGetURL()
    const config = useMemo(() => getConfiguration(breakPoint, {...params}, getURL), [breakPoint]);

    let filterData = isTeam ? data?.teams : data?.persons; // Use data based on isTeam flag

    if (mirs_discipline && mirs_discipline !== ALL_URL.ALL_DISC) {
        filterData = filterData?.filter((itemDiscipline) => {
            return isTeam ? (itemDiscipline?.discipline?.code === mirs_discipline) : (itemDiscipline?.disciplines?.find(disc => disc?.code === mirs_discipline)?.code);

        });
    }

    if (mirs_noc && mirs_noc !== ALL_URL.ALL_NOC) {
        filterData = filterData?.filter(itemOrganisation => itemOrganisation?.organisation?.code === mirs_noc);
    }

    // Create a new map where events are keys and persons are values
    // Assuming filterData contains the filtered data by disc and noc
    let eventsAndPersonsMap = useMemo(() => createEventsAndPersonsMap(filterData, mirs_discipline, isTeam), [filterData, mirs_discipline, isTeam]);

    if (!eventsAndPersonsMap) { return <NotAvailable /> }

    return (
        <>
            <MirsTable
                className="table-schedule"
                data={eventsAndPersonsMap}
                columns={config.columns}
                keyRow='eventUnit-code'
                initialSortBy={config.sortBy}
                id={`mirs-table-teams`}
                showPagination={true}
            // pageSizeCustom={10}
            />
        </>
    )
};

function TableColumns(params, getURL) {
    return ([
        ColumnEvent({params: params, getURL: getURL}),
        ColumnCompetitor({}),
        ColumnDOB({}),
    ])
}

function TableColumnsSM(params, getURL) {
    return ([
        ColumnEvent({params: params, getURL: getURL}),
        ColumnCompetitor({}),
        ColumnDOB({}),
    ])
}

function ColumnEvent({
    disableSort = true,
    className = 'schedule-discipline text-start',
    headerClassName = 'text-start text-nowrap',
    label = 'HeaderEvent', //LabelEvent
    params,
    getURL
}) {
    return ({
        id: 'Event',
        Header: <Label data={label} legendFixed={true} />,
        accessor: 'event.event.order',
        // className: className,
        headerClassName: headerClassName,
        className: 'schedule-event',
        // headerClassName: 'schedule-event',
        disableSortBy: disableSort, //habilita poder ordenar
        Cell: (props) => {
            if(!params && !getURL) {
                return <></>
            }
            let event = props.cell.row.original?.event;
            params["mirs_date"] = ALL_URL.ALL_DAYS
            params["mirs_event"] = event?.code
            let url = getURL(params, "schedule")
            //let url = `/${paramsURL['mirs_competition']}/${paramsURL['mirs_language']}/${'schedule'}/${paramsURL['mirs_discipline']}/${ALL_URL.ALL_DAYS}/${event?.code}`;
            return (<Link to={url} style={{ textDecoration: 'none' }}><div key={event?.code}>{event?.event?.description} </div></Link>)
        }
    })
}

function ColumnCompetitor({
    label = "LabelName",
    disableSort = false
}) {
    return (
        {
            Header: <Label data={label} legendFixed={true} />,
            accessor: 'athlete.TVName',
            disableSortBy: true,
            className: 'schedule-discipline text-start ',
            headerClassName: 'text-start text-nowrap',
            id: 'Name',
            Cell: props => {
                const row = props.cell.row.original
                return row?.persons.map(person => (
                        <div style={{ height: '50px' }}  key={`participant-${row?.event?.event?.code}-${person?.code}`}>
                            <Competitor {...person} hideNoc={true} hidePicture={false} />
                        </div>)
                )
            }
        })
}

function ColumnDOB({
    disableSort = true,
    className = 'text-start',
    headerClassName = 'text-start text-nowrap',
    label = 'HeaderBirthDate',
}) {
    return ({
        Header: <Label data={label} legendFixed={true} />,
        id: 'DOB',
        accessor: 'birthDate',
        className: className,
        headerClassName: headerClassName,
        disableSortBy: disableSort, //habilita poder ordenar
        Cell: (props) => {
            const row = props.cell.row.original
            const formatDateTime = getDateTimeFormat('DayMonthShortYear');
            return row?.persons.map(person => (
                <div style={{ height: '50px' }} key={`participant-${row?.event?.event?.code}-${person?.code}`} >
                    <DateTime datetime={person.birthDate} format={formatDateTime} />
                </div>)
            )
        }
    })
}

function createEventsAndPersonsMap(filteredData, mirs_discipline, isTeam) {
    const eventsAndPersonsMap = new Map();

    if (isTeam) {//Team
        //TODO: iterate to Athlete and extract person and event
        // Iterate through the filtered data and populate the map
        filteredData?.forEach(item => {
            const registeredEvent = item?.registeredEvents[0];
            // Check if the item has registered events
            if (item?.athletes) {
                item?.athletes?.forEach(athleteEntry => {
                    // Create a new person object
                    const person = athleteEntry?.person;
                    // const person = {code: item.code, TVName: item.TVName, name: item.name, shortName: item.shortName, birthDate: item.birthDate};

                    if (eventsAndPersonsMap.has(registeredEvent?.code)) {
                        // If the event already exists in the map, add the person to the array of persons
                        eventsAndPersonsMap.get(registeredEvent?.code).persons.push(person);
                    } else {
                        // If the event is not in the map, create a new array with the person and set it as the value
                        eventsAndPersonsMap.set(registeredEvent?.code, { event: registeredEvent, persons: [person] });
                    }
                });
            }
        });
    } else {//Person
        // Iterate through the filtered data and populate the map
        filteredData?.forEach(item => {
            // Check if the item has registered events
            if (item?.registeredEvents) {
                item?.registeredEvents?.filter(eve => eve?.code?.startsWith(mirs_discipline))?.forEach(eventEntry => {
                    //const eventKey = eventEntry.event?.description || eventEntry.event?.longDescription;
                    const eventKey = eventEntry?.event?.code;//  {eventCode:{event:{}, persons:[]}}
                    // Create a new person object
                    const person = item;
                    // const person = {code: item.code, TVName: item.TVName, name: item.name, shortName: item.shortName, birthDate: item.birthDate};

                    if (eventsAndPersonsMap.has(eventKey)) {
                        // If the event already exists in the map, add the person to the array of persons
                        eventsAndPersonsMap.get(eventKey).persons.push(person);
                    } else {
                        // If the event is not in the map, create a new array with the person and set it as the value
                        eventsAndPersonsMap.set(eventKey, { event: eventEntry, persons: [person] });
                    }
                });
            }
        });
    }
    eventsAndPersonsMap.forEach(value => {
        value?.persons?.sort((a, b) => a?.name?.toLowerCase()?.localeCompare(b?.name.toLowerCase()))
    })
    return eventsAndPersonsMap;
}


const getConfiguration = (breakPoint, params, getURL) => {
    const isMobile = (breakPoint === "sm" || breakPoint === "xs")

    let configuration = {
        columns: [],
        sortBy: [],
        disableSort: false
    };

    configuration.sortBy = [{ id: "Event", desc: false }]

    configuration.columns = isMobile ? TableColumnsSM(params, getURL) : TableColumns(params, getURL)

    return configuration;
}
